/**
 * Inversiones (fondos, precios y aportaciones) y corrección de la foto de patrimonio de un mes.
 * Mismo criterio que el resto de pantallas de edición: cambios al instante en la copia local y al libro en segundo plano.
 */
import { round2 } from '@pf/domain';
import type { Command } from '@pf/workbook';
import { investments } from './app/model.js';
import { fundLines, cashAtMonthEnd, investedAtMonthEnd, accountsAtMonthEnd } from './app/investments-model.js';
import { $, money, esc, monthName, dayLabel, todayIso, signClass, pct } from './ui/format.js';
import { toast, stickyBar } from './ui/feedback.js';
import { frame, setView } from './views.js';
import { pendingBanner, goBack, el, val, checked, parseAmount, amountText, type EditState, type Editor } from './edit-views.js';

const NEW_FUND = '__new__';
const unitsText = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 6 });
const parseUnits = (s: string) => (s.trim() ? parseAmount(s) : NaN);

export function viewInvestments(s: EditState, ed: Editor) {
  frame(s, { title: 'Inversiones', tab: 'home', back: true });
  const lines = fundLines(s.c);
  const tot = investments(s.c);
  const today = todayIso();
  const funds = lines.map(l => l.fund);
  const flows = s.c.wb.flows.map((f, i) => ({ ...f, i })).sort((a, b) => b.date.localeCompare(a.date) || b.i - a.i);
  setView(`${pendingBanner(s)}
    <section class="card">
      <h2>Valor de tus fondos</h2>
      <div class="hero-value">${money(tot.value, { round: true })}</div>
      <div class="split"><div><div class="sub">Aportado</div><div>${money(tot.invested, { round: true })}</div></div>
        <div><div class="sub">Ganancia</div><div class="${signClass(tot.gain)}">${tot.gain > 0 ? '+' : ''}${money(tot.gain, { round: true })}${tot.returnPct === null ? '' : ` · ${pct(tot.returnPct)}`}</div></div></div>
      <div class="sub hint" style="margin-top:10px">El valor sale de tus participaciones × el precio (valor liquidativo) de cada fondo. Actualiza el precio cuando quieras saber cuánto valen hoy: lo ves en la app de tu banco o en la web del fondo.</div>
    </section>
    ${lines.map((l, i) => `<section class="card">
      <h2>${esc(l.fund)}</h2>
      <div class="kpis">
        <div class="kpi"><div class="label">Valor</div><div class="value">${l.value === null ? '—' : money(l.value, { round: true })}</div></div>
        <div class="kpi"><div class="label">Aportado</div><div class="value">${money(l.invested, { round: true })}</div></div>
        <div class="kpi"><div class="label">Ganancia</div><div class="value ${signClass(l.gain)}">${l.gain === null ? '—' : `${l.gain > 0 ? '+' : ''}${money(l.gain, { round: true })}`}</div></div>
      </div>
      <div class="sub" style="margin-top:8px">${unitsText(l.units)} participaciones · precio ${l.price === null ? 'sin poner' : `${l.price.toLocaleString('es-ES', { maximumFractionDigits: 4 })} €`}${l.priceDate ? ` (${esc(dayLabel(l.priceDate))})` : ''}${l.returnPct === null ? '' : ` · ${pct(l.returnPct)}`}</div>
      <div class="split">
        <div class="field"><label for="price${i}">Precio nuevo</label><input id="price${i}" inputmode="decimal" placeholder="${l.price === null ? '0,0000' : esc(String(l.price).replace('.', ','))}"></div>
        <div class="field"><label for="pdate${i}">A día</label><input id="pdate${i}" type="date" value="${today}" max="${today}"></div>
      </div>
      <div class="actions"><button class="btn small" data-price="${i}">Actualizar precio</button></div>
    </section>`).join('')}
    <section class="card">
      <h2>Añadir operación</h2>
      <div class="split">
        <div class="field"><label for="ftype">Tipo</label><select id="ftype"><option value="1">Aportación</option><option value="-1">Reembolso</option></select></div>
        <div class="field"><label for="fdate">Fecha</label><input id="fdate" type="date" value="${today}" max="${today}"></div>
      </div>
      <div class="field"><label for="ffund">Fondo</label><select id="ffund">${funds.map(f => `<option>${esc(f)}</option>`).join('')}<option value="${NEW_FUND}">＋ Fondo nuevo…</option></select></div>
      <div class="field" id="fnewWrap" ${funds.length ? 'hidden' : ''}><label for="fnew">Nombre del fondo nuevo</label><input id="fnew" autocomplete="off" placeholder="Como aparece en tu banco"></div>
      <div class="split">
        <div class="field"><label for="famount">Importe (€)</label><input id="famount" inputmode="decimal" placeholder="0,00"></div>
        <div class="field"><label for="funits">Participaciones</label><input id="funits" inputmode="decimal" placeholder="0,000000"></div>
      </div>
      <div class="sub hint">Las participaciones vienen en la confirmación de la operación. Sin ellas, la app sabe lo que has aportado pero no cuánto vale.</div>
      <div class="actions"><button class="btn" id="addFlow">Guardar operación</button></div>
    </section>
    <section class="card"><h2>Operaciones</h2><div class="list">${flows.map(f => `<div class="row"><div class="main"><div class="title">${esc(f.fund)}</div>
      <div class="meta">${esc(dayLabel(f.date))} · ${f.amount < 0 ? 'Reembolso' : f.amount > 0 ? 'Aportación' : 'Ajuste'}${f.units ? ` · ${unitsText(f.units)} part.` : ''}</div></div>
      <div class="amount ${f.amount < 0 ? 'neg' : ''}">${money(f.amount)}</div></div>`).join('') || '<div class="empty">Sin operaciones</div>'}</div>
      <div class="sub hint">Para corregir o borrar una operación, hazlo en la pestaña Inversiones del libro.</div></section>`);
  if (!funds.length) ($('#ffund') as HTMLSelectElement).value = NEW_FUND;

  $('#view').onchange = e => { if ((e.target as HTMLElement).id === 'ffund') el('fnewWrap')!.hidden = val('ffund') !== NEW_FUND; };
  $('#view').onclick = async e => {
    const b = (e.target as HTMLElement).closest('button');
    if (!b) return;
    if (b.dataset.price) {
      const i = Number(b.dataset.price), l = lines[i]!;
      const price = parseAmount(val(`price${i}`)), date = val(`pdate${i}`) || today;
      if (!Number.isFinite(price) || price <= 0) return toast('Pon el precio (valor liquidativo) del fondo', 'error');
      await ed.edit([{ type: 'set-price', fund: l.fund, price, date }], `Precio de ${l.fund}`);
      toast('✓ Precio actualizado');
    } else if (b.id === 'addFlow') {
      const fund = (val('ffund') === NEW_FUND ? val('fnew') : val('ffund')).trim();
      const sign = Number(val('ftype')) as 1 | -1;
      const amount = parseAmount(val('famount')), units = parseUnits(val('funits')), date = val('fdate');
      if (!fund) return toast('Elige el fondo o escribe el nombre del nuevo', 'error');
      if (!date) return toast('Pon la fecha de la operación', 'error');
      if (!Number.isFinite(amount) || amount === 0) return toast('Pon el importe de la operación', 'error');
      if (val('funits').trim() && !Number.isFinite(units)) return toast('Las participaciones no son un número', 'error');
      const cmd: Command = { type: 'add-flow', flow: { date, fund, amount: round2(sign * Math.abs(amount)), units: Number.isFinite(units) ? sign * Math.abs(units) : null } };
      await ed.edit([cmd], `${sign > 0 ? 'Aportación' : 'Reembolso'} ${fund}`);
      toast(`✓ ${sign > 0 ? 'Aportación' : 'Reembolso'} guardado`);
    }
  };
}

const SOURCE: Record<string, string> = {
  estimated: 'Estimado: inversiones = lo aportado hasta fin de mes',
  auto: 'Guardado automáticamente a principios del mes siguiente',
  manual: 'Corregido a mano (liquidez e inversiones)',
  'manual-inversiones': 'Valor de los fondos corregido a mano',
};

/**
 * Foto de patrimonio de un mes cerrado. La liquidez se calcula con los movimientos (con el desglose por cuenta para
 * compararlo con los extractos); solo se fija a mano si se desmarca. El valor de los fondos sí es de la foto.
 */
export function viewSnapshotEdit(s: EditState, month: string, ed: Editor) {
  frame(s, { title: `Patrimonio · ${monthName(month)}`, tab: 'home', back: true });
  stickyBar('');
  if (!/^\d{4}-\d{2}$/.test(month) || month >= todayIso().slice(0, 7)) {
    setView('<section class="card"><div class="sub">Solo se pueden corregir meses ya cerrados. El mes en curso se calcula solo.</div></section>');
    return;
  }
  const snap = s.c.wb.snapshots.find(x => x.month === month);
  const cash = cashAtMonthEnd(s.c, month), invested = investedAtMonthEnd(s.c, month);
  const accounts = accountsAtMonthEnd(s.c, month);
  const fixedCash = snap?.source === 'manual';
  const start = { auto: !fixedCash, cash: fixedCash ? snap!.cash : cash, investments: snap ? (snap.source === 'estimated' ? invested : snap.investments) : invested };
  const lastDay = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  setView(`${pendingBanner(s)}
    <section class="card">
      <h2>Liquidez a ${lastDay} de ${esc(monthName(month).toLowerCase())}</h2>
      <div class="hero-value">${money(cash)}</div>
      <div class="sub">Calculada con tus movimientos. Compara cada cuenta con el saldo de tu extracto a ese día:</div>
      <div class="table-wrap"><table class="nw acc-month"><thead><tr><th>Cuenta</th><th class="num">Saldo</th><th class="num">Tu parte</th></tr></thead><tbody>
        ${accounts.map(a => `<tr${a.inNetWorth ? '' : ' class="live"'}><td><a href="#/cuenta/${encodeURIComponent(a.name)}">${esc(a.name)}</a>${a.share !== 1 ? ` <span class="sub">(${Math.round(a.share * 100)} %)</span>` : ''}${a.inNetWorth ? '' : ' <span class="sub">(no cuenta)</span>'}</td>
          <td class="num">${money(a.balance)}</td><td class="num">${a.inNetWorth ? money(a.mine) : '—'}</td></tr>`).join('')}
      </tbody></table></div>
      <div class="help">Si una cuenta no cuadra con el extracto, falta o sobra algún movimiento de esa cuenta hasta ese día (o su saldo inicial no es correcto). Al corregirlo, este importe se actualiza solo.</div>
      <label class="check"><input type="checkbox" id="autoCash"${start.auto ? ' checked' : ''}> Usar la liquidez calculada (recomendado)</label>
      <div class="field" id="cashWrap"${start.auto ? ' hidden' : ''}><label for="cash">Liquidez fijada a mano</label><input id="cash" inputmode="decimal" value="${amountText(start.cash)}"></div>
    </section>
    <section class="card">
      <h2>Valor de tus fondos a fin de mes</h2>
      <div class="sub">${snap ? esc(SOURCE[snap.source] ?? (snap.source ? `Origen: ${snap.source}` : 'Sin origen')) : 'No había foto de este mes.'}</div>
      <div class="field"><label for="inv">Inversiones</label><input id="inv" inputmode="decimal" value="${amountText(start.investments)}"></div>
      <div class="help">Lo aportado hasta fin de mes es ${money(invested)}. Si sabes cuánto valían tus fondos ese día (extracto del banco), pon ese valor: así el rendimiento del mes será real.</div>
    </section>`);
  const read = () => ({ auto: checked('autoCash'), cash: round2(parseAmount(val('cash'))), investments: round2(parseAmount(val('inv'))) });
  const bar = () => {
    const now = read();
    el('cashWrap')!.hidden = now.auto;
    const ok = Number.isFinite(now.investments) && (now.auto || Number.isFinite(now.cash));
    const dirty = ok && (!snap || now.auto !== start.auto || now.investments !== start.investments || (!now.auto && now.cash !== start.cash));
    stickyBar(`<button class="btn" id="saveSnap"${dirty ? '' : ' disabled'}>Guardar</button>`);
    el<HTMLButtonElement>('saveSnap')!.onclick = async () => {
      const r = read();
      // Si los fondos siguen siendo lo aportado y la liquidez es la calculada, el mes sigue siendo una estimación
      const source = !r.auto ? 'manual' : snap?.source === 'estimated' && r.investments === invested ? 'estimated' : 'manual-inversiones';
      await ed.edit([{ type: 'upsert-snapshot', snapshot: { month, cash: r.auto ? cash : r.cash, investments: r.investments, source } }], `Patrimonio de ${monthName(month)}`);
      toast('✓ Mes guardado');
      goBack('#/patrimonio');
    };
  };
  $('#view').oninput = bar;
  $('#view').onchange = bar;
  bar();
}
